import { RANGE_END, RANGE_START } from './constants';
import { isRangeEnd, isRangeMarker, isRangeStart, splitRangeMarkersInDiff } from './diff-utils';
import type { DiffSegment } from './types';

/**
 * Normalizes range markers (RANGE_START and RANGE_END) in a diff.
 *
 * This function performs two operations:
 *
 * 1. Splits combined markers: If a range marker (⟬ or ⟭) appears in the same
 *    insert operation as other text (e.g., "Hello⟬world"), it splits them into
 *    separate insert operations. This ensures markers are always isolated and
 *    easy to identify.
 *
 * 2. Repositions misplaced markers: The diff algorithm (diff-match-patch) can
 *    place markers in semantically incorrect positions. For example, if the
 *    original text is "Hello world" and the target is "Hello ⟬beautiful ⟭world",
 *    the diff might produce: insert("⟬beautiful ⟭"), delete(" "), equal("world").
 *    After semantic cleanup, this could become: delete(" "), insert("⟬beautiful ⟭").
 *    The delete now appears BEFORE the RANGE_START marker, which is incorrect.
 *
 *    This function fixes such cases by ensuring:
 *    - RANGE_START appears before all content edits for its id
 *    - RANGE_END appears after all content edits for its id
 *
 * Note: This function does NOT add missing markers. Use `ensureAndNormalizeRangeInDiff`
 * if you need to ensure markers exist.
 */
export function normalizeRangeInDiff(diff: DiffSegment[]): DiffSegment[] {
  // Step 1: Split inserts that contain RANGE_START or RANGE_END mixed with other text
  let result = splitRangeMarkersInDiff(diff);

  // Step 2: Get all unique id values
  const idValues = getUniqueIdValues(result);

  // Step 3: For each id value, ensure proper ordering of range markers
  for (const targetId of idValues) {
    result = normalizeRangeForId(result, targetId);
  }

  return result;
}

/**
 * Gets all unique id values from the diff (excluding equal operations).
 */
function getUniqueIdValues(diff: DiffSegment[]): Set<string> {
  const idValues = new Set<string>();
  for (const change of diff) {
    if (change.op !== 'equal') {
      idValues.add(change.id);
    }
  }
  return idValues;
}

/**
 * Normalizes range markers for a specific id value.
 */
function normalizeRangeForId(diff: DiffSegment[], targetId: string): DiffSegment[] {
  // Find indices of RANGE_START, RANGE_END, and content edits for this id
  let rangeStartIdx = -1;
  let rangeEndIdx = -1;
  let firstContentEditIdx = -1;
  let lastContentEditIdx = -1;

  for (const [i, change] of diff.entries()) {
    if (change.op === 'equal') continue;
    if (change.id !== targetId) continue;

    if (isRangeStart(change)) {
      rangeStartIdx = i;
    } else if (isRangeEnd(change)) {
      rangeEndIdx = i;
    } else if (!isRangeMarker(change)) {
      // This is a content edit (not a range marker)
      if (firstContentEditIdx === -1) {
        firstContentEditIdx = i;
      }
      lastContentEditIdx = i;
    }
  }

  // No content edits for this id - nothing to normalize
  if (firstContentEditIdx === -1) {
    return diff;
  }

  const result = [...diff];

  // Handle RANGE_START: move it to just before first content edit if needed
  if (rangeStartIdx !== -1 && rangeStartIdx > firstContentEditIdx) {
    const [rangeStartDiffSegment] = result.splice(rangeStartIdx, 1);
    if (rangeStartDiffSegment) {
      result.splice(firstContentEditIdx, 0, rangeStartDiffSegment);
    }

    // Recalculate indices after modification
    rangeEndIdx = findRangeEndIdx(result, targetId);
    lastContentEditIdx = findLastContentEditIdx(result, targetId);
  }

  // Handle RANGE_END: move it to just after last content edit if needed
  if (rangeEndIdx !== -1 && rangeEndIdx < lastContentEditIdx) {
    const [rangeEndDiffSegment] = result.splice(rangeEndIdx, 1);
    if (rangeEndDiffSegment) {
      // Removing an earlier element shifts lastContentEditIdx down by 1, so it
      // already points to the slot just after the last content edit.
      result.splice(lastContentEditIdx, 0, rangeEndDiffSegment);
    }
  }

  return result;
}

/**
 * Finds the index of RANGE_END for a given id value.
 */
function findRangeEndIdx(diff: DiffSegment[], targetId: string): number {
  for (const [i, change] of diff.entries()) {
    if (isRangeEnd(change) && change.op !== 'equal' && change.id === targetId) {
      return i;
    }
  }
  return -1;
}

/**
 * Finds the index of the last content edit for a given id value.
 */
function findLastContentEditIdx(diff: DiffSegment[], targetId: string): number {
  let lastIdx = -1;
  for (const [i, change] of diff.entries()) {
    if (change.op !== 'equal' && change.id === targetId && !isRangeMarker(change)) {
      lastIdx = i;
    }
  }
  return lastIdx;
}

/**
 * Ensures range markers exist for all ids with content edits, then normalizes.
 *
 * For each unique id that has content edits (insert/delete that aren't range markers):
 * - If neither marker exists: adds both around all content edits, then normalizes
 * - If at least one marker exists: adds missing marker, then reverts diffs outside the range
 *
 * Reversion behavior (only when at least one marker originally existed):
 * - `insert` operations outside the range are removed (don't add hallucinated text)
 * - `delete` operations outside the range become `equal` (keep original text)
 *
 * Important: For ids WITH existing markers, we respect the marker positions as explicit
 * boundaries and do NOT normalize (which would move markers to bracket all content).
 * For ids WITHOUT markers, we add markers around all content and normalize as before.
 */
export function ensureFixAndNormalizeRangeInDiff(diff: DiffSegment[]): DiffSegment[] {
  // Step 1: Split any combined range markers first
  let result = splitRangeMarkersInDiff(diff);

  // Step 2: Get all unique id values
  const idValues = getUniqueIdValues(result);

  // Step 3: For each id value, check if markers exist before ensuring
  const idsWithExistingMarkers = new Set<string>();
  for (const targetId of idValues) {
    const hasMarker = result.some(c => c.op !== 'equal' && c.id === targetId && isRangeMarker(c));
    if (hasMarker) {
      idsWithExistingMarkers.add(targetId);
    }
  }

  // Step 4: For each id value, ensure range markers exist
  for (const targetId of idValues) {
    result = ensureRangeMarkersForId(result, targetId);
  }

  // Step 5: For ids that had markers, revert diffs outside the range
  // We do NOT normalize these - the marker positions are explicit boundaries
  for (const targetId of idsWithExistingMarkers) {
    result = revertDiffsOutsideRange(result, targetId);
  }

  // Step 6: For ids that did NOT have markers, normalize to ensure proper ordering
  // This handles cases where markers were auto-added and may need repositioning
  const idsWithoutExistingMarkers = [...idValues].filter(id => !idsWithExistingMarkers.has(id));
  for (const targetId of idsWithoutExistingMarkers) {
    result = normalizeRangeForId(result, targetId);
  }

  return result;
}

/**
 * Ensures RANGE_START and RANGE_END exist for a specific id value.
 *
 * Behavior:
 * - If neither marker exists: adds both around all content edits (original behavior)
 * - If at least one marker exists: adds the missing marker at the boundary of "inside" content
 *
 * "Inside" content is determined by the existing marker:
 * - If only RANGE_START exists: inside = content edits after RANGE_START
 * - If only RANGE_END exists: inside = content edits before RANGE_END
 */
function ensureRangeMarkersForId(diff: DiffSegment[], targetId: string): DiffSegment[] {
  let rangeStartIdx = -1;
  let rangeEndIdx = -1;
  let firstContentEditIdx = -1;
  let lastContentEditIdx = -1;

  for (const [i, change] of diff.entries()) {
    if (change.op === 'equal') continue;
    if (change.id !== targetId) continue;

    if (isRangeStart(change)) {
      rangeStartIdx = i;
    } else if (isRangeEnd(change)) {
      rangeEndIdx = i;
    } else if (!isRangeMarker(change)) {
      if (firstContentEditIdx === -1) {
        firstContentEditIdx = i;
      }
      lastContentEditIdx = i;
    }
  }

  // No content edits for this id - nothing to add
  if (firstContentEditIdx === -1) {
    return diff;
  }

  const hasRangeStart = rangeStartIdx !== -1;
  const hasRangeEnd = rangeEndIdx !== -1;

  // If neither marker exists, add both around all content edits (original behavior)
  if (!hasRangeStart && !hasRangeEnd) {
    const result = [...diff];
    // Add RANGE_END first (so indices don't shift for RANGE_START insertion)
    const rangeEndDiffSegment: DiffSegment = { id: targetId, op: 'insert', text: RANGE_END };
    result.splice(lastContentEditIdx + 1, 0, rangeEndDiffSegment);
    // Add RANGE_START
    const rangeStartDiffSegment: DiffSegment = { id: targetId, op: 'insert', text: RANGE_START };
    result.splice(firstContentEditIdx, 0, rangeStartDiffSegment);
    return result;
  }

  // At least one marker exists - add the missing one at the correct boundary
  const result = [...diff];

  if (!hasRangeEnd) {
    // Only RANGE_START exists - add RANGE_END after the last content edit that's after RANGE_START
    let lastInsideContentIdx = -1;
    for (const [i, change] of result.entries()) {
      if (change.op === 'equal') continue;
      if (change.id !== targetId) continue;
      if (i > rangeStartIdx && !isRangeMarker(change)) {
        lastInsideContentIdx = i;
      }
    }
    // If there's inside content, add RANGE_END after it; otherwise add immediately after RANGE_START
    const insertIdx = lastInsideContentIdx !== -1 ? lastInsideContentIdx + 1 : rangeStartIdx + 1;
    const rangeEndDiffSegment: DiffSegment = { id: targetId, op: 'insert', text: RANGE_END };
    result.splice(insertIdx, 0, rangeEndDiffSegment);
  }

  if (!hasRangeStart) {
    // Only RANGE_END exists - add RANGE_START before the first content edit that's before RANGE_END
    // Note: rangeEndIdx may have shifted if we just added RANGE_END above, so recalculate
    const currentRangeEndIdx = result.findIndex(
      c => c.op !== 'equal' && c.id === targetId && isRangeEnd(c)
    );
    let firstInsideContentIdx = -1;
    for (const [i, change] of result.entries()) {
      if (change.op === 'equal') continue;
      if (change.id !== targetId) continue;
      if (i < currentRangeEndIdx && !isRangeMarker(change)) {
        firstInsideContentIdx = i;
        break;
      }
    }
    // If there's inside content, add RANGE_START before it; otherwise add immediately before RANGE_END
    const insertIdx = firstInsideContentIdx !== -1 ? firstInsideContentIdx : currentRangeEndIdx;
    const rangeStartDiffSegment: DiffSegment = { id: targetId, op: 'insert', text: RANGE_START };
    result.splice(insertIdx, 0, rangeStartDiffSegment);
  }

  return result;
}

/**
 * Ensures every edit ID with content diffs has range markers, adding them
 * without creating interleaving ranges with existing markers.
 *
 * For each edit ID that has content diffs but no range markers:
 * 1. Walks the diff tracking which existing ranges are "open" (range depth signature)
 * 2. Groups the unmarked ID's content edits into contiguous runs that share the
 *    same range depth signature — a new group starts when an existing range boundary
 *    (START or END from another ID) is crossed
 * 3. Inserts RANGE_START/RANGE_END around each group
 *
 * This guarantees new marker pairs are always fully inside or fully outside every
 * existing range — never crossing a boundary (no interleaving).
 */
export function ensureNonOverlappingRangeMarkers(diff: DiffSegment[]): DiffSegment[] {
  // Step 1: Split any combined range markers first
  let result = splitRangeMarkersInDiff(diff);

  // Step 2: Find edit IDs that have content diffs but no range markers
  const idsWithContent = new Set<string>();
  const idsWithMarkers = new Set<string>();

  for (const change of result) {
    if (change.op === 'equal') continue;
    if (isRangeMarker(change)) {
      idsWithMarkers.add(change.id);
    } else {
      idsWithContent.add(change.id);
    }
  }

  const unmarkedIds = [...idsWithContent].filter(id => !idsWithMarkers.has(id));
  if (unmarkedIds.length === 0) {
    return result;
  }

  // Step 3: For each unmarked ID, add non-overlapping range markers
  for (const targetId of unmarkedIds) {
    result = addNonOverlappingMarkersForId(result, targetId);
  }

  return result;
}

/**
 * Adds range markers around content edits for a specific ID, splitting into
 * multiple marker pairs at existing range boundaries to avoid interleaving.
 */
function addNonOverlappingMarkersForId(diff: DiffSegment[], targetId: string): DiffSegment[] {
  // Track groups of content edit indices. A new group starts when any
  // existing range boundary (START or END from another ID) is crossed
  // between two of this ID's content edits.
  type Group = { firstIdx: number; lastIdx: number };
  const groups: Group[] = [];

  let rangeBoundaryCrossed = false;
  let currentGroup: Group | null = null;

  for (const [i, change] of diff.entries()) {
    // Track existing range boundaries (from other IDs)
    if (change.op !== 'equal' && change.id !== targetId && isRangeMarker(change)) {
      rangeBoundaryCrossed = true;
    }

    // Check if this is a content edit for our target ID
    if (change.op !== 'equal' && change.id === targetId && !isRangeMarker(change)) {
      if (currentGroup === null) {
        // Start first group
        currentGroup = { firstIdx: i, lastIdx: i };
        rangeBoundaryCrossed = false;
      } else if (!rangeBoundaryCrossed) {
        // No boundary crossed — extend current group
        currentGroup.lastIdx = i;
      } else {
        // Boundary crossed — close current group, start new one
        groups.push(currentGroup);
        currentGroup = { firstIdx: i, lastIdx: i };
        rangeBoundaryCrossed = false;
      }
    }
  }

  // Close final group
  if (currentGroup !== null) {
    groups.push(currentGroup);
  }

  if (groups.length === 0) {
    return diff;
  }

  // Insert markers in reverse order to avoid index shifting
  const result = [...diff];
  for (let g = groups.length - 1; g >= 0; g--) {
    const group = groups[g];
    if (!group) continue;
    const endMarker: DiffSegment = { id: targetId, op: 'insert', text: RANGE_END };
    result.splice(group.lastIdx + 1, 0, endMarker);
    const startMarker: DiffSegment = { id: targetId, op: 'insert', text: RANGE_START };
    result.splice(group.firstIdx, 0, startMarker);
  }

  return result;
}

/**
 * Reverts diffs that fall outside the range markers for a specific id.
 *
 * For diffs outside the range (before RANGE_START or after RANGE_END):
 * - `insert` operations are removed entirely (don't add hallucinated text)
 * - `delete` operations are converted to `equal` (keep original text)
 *
 * This handles the case where an LLM hallucinates changes outside the
 * explicitly marked range.
 *
 * IMPORTANT:
 * 1. If there are no content edits at all for this id (just markers),
 *    we skip reversion. This handles empty ranges like ⟬⟭ which are position
 *    markers, not content brackets.
 * 2. Content edits that are IMMEDIATELY adjacent to the markers (no equal text
 *    separating them) are NOT reverted. They might be part of the same edit
 *    that was split by the diff algorithm.
 * 3. Only content edits that are SEPARATED from the range by equal text are
 *    reverted - these are likely hallucinations.
 */
function revertDiffsOutsideRange(diff: DiffSegment[], targetId: string): DiffSegment[] {
  // Find RANGE_START and RANGE_END positions
  let rangeStartIdx = -1;
  let rangeEndIdx = -1;
  let hasAnyContentEdits = false;

  for (const [i, change] of diff.entries()) {
    if (change.op === 'equal') continue;
    if (change.id !== targetId) continue;

    if (isRangeStart(change)) {
      rangeStartIdx = i;
    } else if (isRangeEnd(change)) {
      rangeEndIdx = i;
    } else if (!isRangeMarker(change)) {
      hasAnyContentEdits = true;
    }
  }

  // If both markers don't exist, nothing to revert (this shouldn't happen after ensureRangeMarkersForId)
  if (rangeStartIdx === -1 || rangeEndIdx === -1) {
    return diff;
  }

  // If there are no content edits at all for this id (just markers),
  // skip reversion. The markers are just position markers.
  if (!hasAnyContentEdits) {
    return diff;
  }

  // For each change, determine if it's separated from the range by equal text.
  // We check per-change rather than using global flags, because a change might
  // be immediately adjacent to RANGE_START even if other changes are separated.

  // Check if a change at index i (before RANGE_START) is separated from RANGE_START
  // by equal text. A change is "separated" if there's any equal text between it and RANGE_START.
  function isSeparatedFromRangeStart(changeIdx: number): boolean {
    for (let j = changeIdx + 1; j < rangeStartIdx; j++) {
      const c = diff[j];
      if (c && c.op === 'equal') {
        return true;
      }
    }
    return false;
  }

  // Check if a change at index i (after RANGE_END) is separated from RANGE_END
  // by equal text. A change is "separated" if there's any equal text between RANGE_END and it.
  function isSeparatedFromRangeEnd(changeIdx: number): boolean {
    for (let j = rangeEndIdx + 1; j < changeIdx; j++) {
      const c = diff[j];
      if (c && c.op === 'equal') {
        return true;
      }
    }
    return false;
  }

  const result: DiffSegment[] = [];

  for (const [i, change] of diff.entries()) {
    // Always keep equal operations and operations for other ids
    if (change.op === 'equal' || change.id !== targetId) {
      result.push(change);
      continue;
    }

    // Always keep the range markers themselves
    if (isRangeMarker(change)) {
      result.push(change);
      continue;
    }

    // Check if this change is outside the range
    const isBeforeRange = i < rangeStartIdx;
    const isAfterRange = i > rangeEndIdx;

    // Only revert if there's equal text separating THIS SPECIFIC change from the range
    // Adjacent changes might be part of the same edit split by the diff algorithm
    const shouldRevert =
      (isBeforeRange && isSeparatedFromRangeStart(i)) ||
      (isAfterRange && isSeparatedFromRangeEnd(i));

    if (shouldRevert) {
      if (change.op === 'delete') {
        // Convert delete to equal (keep original text)
        result.push({ op: 'equal', text: change.text });
      }
      // Insert operations outside are simply not added (removed)
    } else {
      // Inside the range OR adjacent to range - keep the change
      result.push(change);
    }
  }

  return result;
}
